import { useState, useEffect, useRef } from 'react';
import { Section, Lesson } from '@/lib/types';

interface CourseSidebarProps {
    sections: Section[];
    lessons: Lesson[];
    currentLessonId?: number;
    completedLessonIds: number[];
    onSelectLesson: (lesson: Lesson) => void;
    title: string;
}

export default function CourseSidebar({
    sections,
    lessons,
    currentLessonId,
    completedLessonIds,
    onSelectLesson,
    title,
}: CourseSidebarProps) {
    const [expandedSections, setExpandedSections] = useState<number[]>([]);
    const currentLessonRef = useRef<HTMLButtonElement>(null);

    // Initialize expanded sections or auto-expand current lesson's section
    useEffect(() => {
        if (currentLessonId) {
            const currentLesson = lessons.find(l => l.id === currentLessonId);
            if (currentLesson) {
                setExpandedSections(prev =>
                    prev.includes(currentLesson.section) ? prev : [...prev, currentLesson.section]
                );
            }
        } else if (sections.length > 0 && expandedSections.length === 0) {
            // Default expand first section if nothing selected
            setExpandedSections([sections[0].id]);
        }
    }, [currentLessonId, sections, lessons]);

    useEffect(() => {
        currentLessonRef.current?.scrollIntoView({ block: 'nearest' });
    }, [currentLessonId, expandedSections]);

    const toggleSection = (sectionId: number) => {
        setExpandedSections(prev =>
            prev.includes(sectionId)
                ? prev.filter(id => id !== sectionId)
                : [...prev, sectionId]
        );
    };

    const getSectionLessons = (sectionId: number) => {
        return lessons
            .filter(l => l.section === sectionId)
            .sort((a, b) => a.order - b.order);
    };

    return (
        <div className="bg-white border-r border-gray-200 h-full flex flex-col w-80 flex-shrink-0">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
                <h2 className="font-bold text-gray-900 truncate" title={title}>
                    {title}
                </h2>
                <div className="mt-2 text-xs text-gray-500 font-medium">
                    {completedLessonIds.length} / {lessons.length} Lessons Completed
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                    <div
                        className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${lessons.length ? (completedLessonIds.length / lessons.length) * 100 : 0}%` }}
                    />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto">
                {sections.map((section) => {
                    const sectionLessons = getSectionLessons(section.id);
                    const isExpanded = expandedSections.includes(section.id);

                    return (
                        <div key={section.id} className="border-b border-gray-100">
                            <button
                                onClick={() => toggleSection(section.id)}
                                className="w-full px-4 py-3 flex justify-between items-center bg-white hover:bg-gray-50 transition-colors"
                            >
                                <div className="flex-1 text-left">
                                    <span className="font-semibold text-sm text-gray-800 block">
                                        {section.title}
                                    </span>
                                    <span className="text-xs text-gray-500 mt-0.5 block">
                                        {sectionLessons.filter(l => completedLessonIds.includes(l.id)).length}/{sectionLessons.length} Completed
                                    </span>
                                </div>
                                <svg
                                    className={`w-4 h-4 text-gray-400 transform transition-transform ${isExpanded ? 'rotate-180' : ''
                                        }`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {isExpanded && (
                                <div className="bg-gray-50 py-1">
                                    {sectionLessons.length === 0 ? (
                                        <div className="px-4 py-2 text-xs text-gray-400 italic">
                                            No lessons in this section
                                        </div>
                                    ) : (
                                        sectionLessons.map((lesson) => {
                                            const isCompleted = completedLessonIds.includes(lesson.id);
                                            const isCurrent = currentLessonId === lesson.id;

                                            return (
                                                <button
                                                    key={lesson.id}
                                                    ref={isCurrent ? currentLessonRef : undefined}
                                                    onClick={() => onSelectLesson(lesson)}
                                                    className={`w-full px-4 py-2 flex items-start space-x-3 hover:bg-gray-100 transition-colors ${isCurrent ? 'bg-blue-50 border-l-4 border-blue-600' : 'border-l-4 border-transparent'
                                                        }`}
                                                >
                                                    <div className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded-full border flex items-center justify-center ${isCompleted
                                                            ? 'bg-green-500 border-green-500'
                                                            : 'border-gray-300'
                                                        }`}>
                                                        {isCompleted && (
                                                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <span className={`text-sm text-left ${isCurrent ? 'text-blue-700 font-medium' : 'text-gray-600'
                                                        }`}>
                                                        {lesson.title}
                                                    </span>
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
